import React from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Box } from '@node-space/ui-components'
import { SidePanelSummary } from 'components/SidePanel/SidePanelSummary'
import { DateFormat } from 'constants/General'
import { formatAmount } from 'pages/Rewards/utils'
import { type Invoice } from './types'
import { getInvoiceStatusTag } from './utils'

interface BillingSidePanelDetailsProps {
  invoice: Invoice
}

export const BillingSidePanelDetails = ({ invoice }: BillingSidePanelDetailsProps) => {
  const { t } = useTranslation()

  const status = getInvoiceStatusTag(invoice.status, t)

  const rows = [
    { label: t('billing.invoiceId'), content: invoice?.id, copyText: invoice?.id },
    {
      label: t('billing.invoiceNumber'),
      content: invoice?.invoiceNumber,
      copyText: invoice?.invoiceNumber,
    },
    {
      label: t('billing.billableMonth'),
      content: dayjs(invoice.billableMonth).format(DateFormat.MMM_YYYY),
    },
    {
      label: t('billing.dataFetchedAt'),
      content: dayjs(invoice.dataFetchedAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM),
    },
    { label: t('status'), content: status.label },
    { label: t('billing.currency'), content: invoice?.currency },
    {
      label: t('billing.amount'),
      content: invoice.amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    },
  ]

  return (
    <Box flexCol gap={24}>
      <SidePanelSummary rows={rows} />

      {invoice?.breakdown?.length > 0 && (
        <Box flexCol gap={12}>
          <p className="text-h4 text-foreground-prominent">{t('billing.invoiceBreakdown')}</p>
          {invoice.breakdown?.map(item => (
            <Box key={item.description} flex justify="between" className="px-4">
              <p className="text-body-regular text-foreground-prominent">{item.description}</p>
              <p className="text-body-regular text-foreground-prominent">
                {formatAmount(item.amount)}
              </p>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
